import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { adminApiHeader, finalRouteApi } from '../../components/CommonApis/CommonApiUrl';
import { toast } from 'react-toastify';
import AdminSwitch from '../../components/CommonComponents/AdminSwitch';
import AdminCustomButton from '../../components/CommonComponents/AdminCustomButton';
import AdminProcessButton from '../../components/CommonComponents/AdminProcessButton';
import FormContainer from '../../components/CommonComponents/FormContainer';
import Wrapper from '../../components/CommonComponents/Wrapper';
import MessageHeading from '../../components/CommonComponents/MessageHeading';
import MessageLabel from '../../components/CommonComponents/MessageLabel';
import AdminDeleteModal from '../../components/CommonComponents/AdminDeleteModal';
import { IoCheckmarkCircle, IoCloseCircle, IoWarning } from 'react-icons/io5';
import { BsPlugin } from 'react-icons/bs';
import './PluginsManagement.css';

const PluginsManagement = () => {
  const [plugins, setPlugins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [selectedPlugin, setSelectedPlugin] = useState(null);
  const [baseAppConfig, setBaseAppConfig] = useState(null);

  // Predefined plugins list with metadata
  const predefinedPlugins = [
    {
      id: 'custom_fields',
      name: 'Custom Fields',
      description: 'Add custom fields to any form in the admin panel',
      icon: '📝',
      category: 'form_enhancement',
      dependencies: [],
      price: 0,
      configurable: true,
      configPath: '/admin/custom-fields'
    },
    {
      id: 'live_streaming',
      name: 'Advanced Live Streaming',
      description: 'Enhanced live streaming capabilities with multi-bitrate support',
      icon: '📹',
      category: 'streaming',
      dependencies: [],
      price: 99.99,
      configurable: true,
      configPath: '/admin/live-streaming-settings'
    },
    {
      id: 'ads_manager',
      name: 'Advertisement Manager',
      description: 'Comprehensive ad management with SSAI support',
      icon: '📢',
      category: 'monetization',
      dependencies: [],
      price: 149.99,
      configurable: true,
      configPath: '/admin/ads-settings'
    },
    {
      id: 'analytics_pro',
      name: 'Analytics Pro',
      description: 'Advanced analytics and reporting features',
      icon: '📊',
      category: 'analytics',
      dependencies: [],
      price: 79.99,
      configurable: true,
      configPath: '/admin/analytics-pro'
    },
    {
      id: 'ssai',
      name: 'Server-Side Ad Insertion',
      description: 'Seamless ad insertion for better user experience',
      icon: '🎯',
      category: 'monetization',
      dependencies: ['ads_manager'],
      price: 199.99,
      configurable: true,
      configPath: '/admin/ssai-settings'
    }
  ];

  useEffect(() => {
    fetchPluginsStatus();
    fetchBaseAppConfig();
  }, []);

  const fetchBaseAppConfig = async () => {
    try {
      const response = await axios.get(
        `${finalRouteApi}/baseapp/config`,
        { headers: adminApiHeader }
      );
      setBaseAppConfig(response.data);
    } catch (error) {
      console.error('Error fetching BaseApp config:', error);
    }
  };

  const fetchPluginsStatus = async () => {
    setLoading(true);
    try {
      // Fetch plugin status from backend
      const response = await axios.get(
        `${finalRouteApi}/plugins/status`,
        { headers: adminApiHeader }
      );
      
      // Merge with predefined plugins
      const mergedPlugins = predefinedPlugins.map(plugin => {
        const status = response.data.plugins?.find(p => p.id === plugin.id);
        return {
          ...plugin,
          enabled: status?.enabled || false,
          license_status: status?.license_status || 'inactive',
          expiry_date: status?.expiry_date,
          tenant_id: response.data.tenant_id,
          // Normalize config paths to current admin app
          configPath: plugin.id === 'custom_fields' ? '/custom-fields' : (plugin.id === 'ssai' ? '/ssai-settings' : plugin.configPath)
        };
      });
      
      setPlugins(mergedPlugins);
    } catch (error) {
      console.error('Error fetching plugins status:', error);
      // Show empty state or cached plugins
      setPlugins(predefinedPlugins.map(p => ({ ...p, enabled: false, license_status: 'inactive' })));
    } finally {
      setLoading(false);
    }
  };

  const handlePluginToggle = async (plugin, enabled) => {
    if (enabled && plugin.price > 0 && plugin.license_status !== 'active') {
      // Show activation modal for paid plugins
      setSelectedPlugin(plugin);
      setShowActivationModal(true);
      return;
    }

    if (enabled && plugin.dependencies.length > 0) {
      // Check dependencies
      const unmetDeps = plugin.dependencies.filter(dep =>
        !plugins.find(p => p.id === dep && p.enabled)
      );

      if (unmetDeps.length > 0) {
        toast.error(`Please enable required plugins first: ${unmetDeps.join(', ')}`);
        return;
      }
    }

    setProcessing({ ...processing, [plugin.id]: true });

    try {
      let response;

      if (plugin.id === 'custom_fields') {
        // Use the Custom Fields specific toggle endpoint
        response = await axios.patch(
          `${finalRouteApi}/custom-fields/toggle`,
          { enabled },
          { headers: adminApiHeader }
        );
      } else {
        // Use generic plugin toggle endpoint for other plugins
        response = await axios.patch(
          `${finalRouteApi}/plugins/${plugin.id}/toggle`,
          { enabled },
          { headers: adminApiHeader }
        );
      }

      if (response.data.status) {
        // Update local state
        setPlugins(prev => prev.map(p =>
          p.id === plugin.id
            ? { ...p, enabled, license_status: enabled ? 'active' : 'inactive' }
            : p
        ));

        toast.success(`Plugin ${enabled ? 'enabled' : 'disabled'} successfully`);
        // Reload the current page
        window.location.reload();

        // If Custom Fields was enabled, show info message
        if (plugin.id === 'custom_fields' && enabled) {
          toast.success('Custom Fields enabled successfully! You may need to refresh to see sidebar updates.');
        }

        // Notify BaseApp API if needed
        if (baseAppConfig?.webhook_url) {
          await notifyBaseApp(plugin, enabled ? 'enabled' : 'disabled');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error toggling plugin');
    } finally {
      setProcessing({ ...processing, [plugin.id]: false });
    }
  };

  const notifyBaseApp = async (plugin, action) => {
    try {
      await axios.post(baseAppConfig.webhook_url, {
        tenant_id: plugin.tenant_id,
        plugin_id: plugin.id,
        action,
        timestamp: new Date().toISOString()
      }, {
        headers: {
          'X-BaseApp-Token': baseAppConfig.api_token
        }
      });
    } catch (error) {
      console.error('Error notifying BaseApp:', error);
    }
  };

  const handleActivatePlugin = async () => {
    if (!selectedPlugin) return;

    setProcessing({ ...processing, activation: true });

    try {
      // Redirect to BaseApp payment page or handle license activation
      const response = await axios.post(
        `${finalRouteApi}/plugins/${selectedPlugin.id}/activate`,
        {},
        { headers: adminApiHeader }
      );

      if (response.data.redirect_url) {
        window.location.href = response.data.redirect_url;
      } else if (response.data.status) {
        toast.success('Plugin activated successfully');
        setShowActivationModal(false);
        fetchPluginsStatus();
        // Reload the current page
        window.location.reload();

      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error activating plugin');
    } finally {
      setProcessing({ ...processing, activation: false });
    }
  };

  const getLicenseStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="badge badge-success"><IoCheckmarkCircle /> Active</span>;
      case 'expired':
        return <span className="badge badge-danger"><IoCloseCircle /> Expired</span>;
      case 'trial':
        return <span className="badge badge-warning"><IoWarning /> Trial</span>;
      default:
        return <span className="badge badge-secondary">Inactive</span>;
    }
  };

  const getCategoryBadge = (category) => {
    const categoryColors = {
      form_enhancement: 'primary',
      streaming: 'info',
      monetization: 'success',
      analytics: 'warning'
    };

    return (
      <span className={`badge badge-${categoryColors[category] || 'secondary'}`}>
        {category.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <FormContainer>
        <Wrapper>
          <div className="text-center p-5">
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </Wrapper>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <Wrapper>
        <div className="plugins-management">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <MessageHeading>
              <BsPlugin className="mr-2" /> Plugins Management
            </MessageHeading>
            <AdminCustomButton
              type="button"
              className="btn-primary"
              onClick={() => window.open(baseAppConfig?.marketplace_url || '#', '_blank')}
            >
              Browse Marketplace
            </AdminCustomButton>
          </div>

          <div className="row">
            {plugins.map(plugin => (
              <div key={plugin.id} className="col-md-6 col-lg-4 mb-4">
                <div className={`plugin-card ${plugin.enabled ? 'active' : ''}`}>
                  <div className="plugin-header">
                    <div className="plugin-icon">{plugin.icon}</div>
                    <div className="plugin-badges">
                      {getCategoryBadge(plugin.category)}
                      {plugin.price > 0 && getLicenseStatusBadge(plugin.license_status)}
                    </div>
                  </div>

                  <div className="plugin-body">
                    <h5 className="plugin-title">{plugin.name}</h5>
                    <p className="plugin-description">{plugin.description}</p>

                    {plugin.price > 0 && (
                      <div className="plugin-price">
                        <MessageLabel>Price:</MessageLabel>
                        <span className="price-value">${plugin.price}/month</span>
                      </div>
                    )}

                    {plugin.dependencies.length > 0 && (
                      <div className="plugin-dependencies">
                        <MessageLabel>Requires:</MessageLabel>
                        <span>{plugin.dependencies.join(', ')}</span>
                      </div>
                    )}

                    {plugin.expiry_date && (
                      <div className="plugin-expiry">
                        <MessageLabel>Expires:</MessageLabel>
                        <span>{new Date(plugin.expiry_date).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>

                  <div className="plugin-footer">
                    <AdminSwitch
                      label="Enabled"
                      checked={plugin.enabled}
                      onChange={(e) => handlePluginToggle(plugin, e.target.checked)}
                      disabled={processing[plugin.id]}
                    />

                    {plugin.configurable && plugin.enabled && (
                      <AdminCustomButton
                        type="button"
                        className="btn-sm btn-outline-primary"
                        onClick={() => window.location.href = plugin.configPath}
                      >
                        Configure
                      </AdminCustomButton>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Wrapper>

      {/* Activation Modal */}
      {showActivationModal && selectedPlugin && (
        <AdminDeleteModal
          show={showActivationModal}
          onHide={() => setShowActivationModal(false)}
          title="Activate Plugin"
          message={
            <div>
              <p>This plugin requires a license to activate.</p>
              <p><strong>{selectedPlugin.name}</strong></p>
              <p>Price: ${selectedPlugin.price}/month</p>
              <p>You will be redirected to complete the payment.</p>
            </div>
          }
          onConfirm={handleActivatePlugin}
          confirmText="Proceed to Payment"
          cancelText="Cancel"
          processing={processing.activation}
        />
      )}
    </FormContainer>
  );
};

export default PluginsManagement;
